/**
 * useCameraStore — Zustand store for camera / view-mode state.
 * Replaces the key state from the 69KB camera-system.js (desktop modes only).
 */

import { create } from 'zustand';
import * as THREE from 'three';

import { useStoreyStore } from './useStoreyStore';

export interface SavedCameraState {
  position: THREE.Vector3;
  /** Walk mode stores quaternion so look direction is also restored. */
  quaternion?: THREE.Quaternion;
  /** Orbit modes store the orbit target (the point being orbited). */
  target?: THREE.Vector3;
}

export type CameraMode =
  | 'walk'              // WASD / first-person; look by left-drag (no pointer lock)
  | 'orbit'             // free orbit — alias kept for internal use
  | 'modelview'         // orbit around scene — toolbar "Model View" button
  | 'topdown'           // orthographic top-down
  | 'perspective_topdown'; // tilted top-down orbit

interface CameraState {
  mode: CameraMode;

  /** Per-mode saved camera state — restored when the user returns to a mode. */
  savedPositions: Partial<Record<CameraMode, SavedCameraState>>;

  /**
   * Set right before a viewpoint navigation that also switches mode, and
   * consumed by WalkControls'/OrbitRig's mount-restore effect for the new
   * mode — takes priority over savedPositions/defaults so the just-mounted
   * rig doesn't race the incoming viewpoint with a stale/default position.
   */
  pendingArrival: SavedCameraState | null;

  /**
   * Set while an outside-the-Canvas UI (currently: left-button drag on the
   * 2D floorplan tab, FloorPlanVector.tsx) is actively repositioning the
   * camera every pointermove. R3FScene.tsx ORs this into its adaptive
   * frameloop so the viewport keeps rendering at full speed even though the
   * mouse pointer never enters the canvas itself — frameloop would otherwise
   * stay 'demand' (pointer-outside) and visibly lag behind the drag.
   */
  externalDragActive: boolean;

  // ─── Actions ─────────────────────────────────────────────────────────────
  setMode(mode: CameraMode): void;
  setPendingArrival(state: SavedCameraState | null): void;
  setExternalDragActive(active: boolean): void;
  savePosition(
    mode: CameraMode,
    position: THREE.Vector3,
    quaternion?: THREE.Quaternion,
    target?: THREE.Vector3,
  ): void;
  /**
   * Forget the orbit-based modes' saved poses, so the next entry into each one
   * frames the scene from scratch. Called on a genuine scene switch — see
   * utils/resetSceneState.ts.
   *
   * WALK IS DELIBERATELY KEPT. Its pose is not a view the user arranged, it is
   * the scene's authored spawn point (params.headPosition), and R3FScene3D
   * writes it for the incoming scene DURING RENDER — which is before the effect
   * that calls this. Clearing walk here would throw that write away and drop the
   * user at the previous scene's spawn, or at the origin.
   */
  clearOrbitPositions(): void;

  /**
   * Returns the floor height closest to (and below) the given world position.
   * Used by the drag-drop placement logic — mirrors camera-system.js getCloserFloorHeight().
   *
   * Reads the storey list from useStoreyStore. Kept on this store (rather than
   * moved) because several call sites — drop placement, teleport landing,
   * mounting height — already depend on it here. (Floorplan recentering no
   * longer does: see useFloorplanRecenterCamera.ts for why.)
   */
  getCloserFloorHeight(position: THREE.Vector3): number;
}

export const useCameraStore = create<CameraState>((set, get) => ({
  // 'walk' is the default — matches the toolbar's Walk button being selected on load.
  // Users switch to 'modelview' (orbit) for editing, walk back in for walkthroughs.
  mode: 'walk',

  savedPositions: {},
  pendingArrival: null,
  externalDragActive: false,

  setPendingArrival(state) {
    set({ pendingArrival: state });
  },

  setExternalDragActive(active) {
    set({ externalDragActive: active });
  },

  savePosition(mode, position, quaternion, target) {
    set((state) => ({
      savedPositions: {
        ...state.savedPositions,
        [mode]: {
          position:   position.clone(),
          quaternion: quaternion?.clone(),
          target:     target?.clone(),
        },
      },
    }));
  },

  clearOrbitPositions() {
    const { walk } = get().savedPositions;
    // Rebuilt from scratch rather than deleting keys, so a mode added later is
    // cleared by default instead of silently surviving scene switches.
    set({ savedPositions: walk ? { walk } : {} });
  },

  setMode(mode) {
    set({ mode });
  },

  getCloserFloorHeight(position) {
    const { storeys } = useStoreyStore.getState();
    // No storeys derived yet (empty scene, or models still streaming in) —
    // ground level is the only sensible answer, and matches the behaviour
    // before storeys existed, when this store held a single default storey
    // whose floor was at y=0.
    if (storeys.length === 0) return 0;

    // Highest storey floor at or below the point.
    const below = storeys.filter((s) => s.elevation <= position.y);
    if (below.length === 0) return storeys[0].elevation;
    return Math.max(...below.map((s) => s.elevation));
  },
}));
